import React, {
  useState, useCallback, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { DateTime } from 'luxon';

import { prepareDragShiftUpdatePayload } from '../scheduleHelpers';
import { resourceAxisPropType, resourceRowPropType } from './resourceAxis.propTypes';

import {
  setBiweeklyModalConfig,
  updateShift,
} from '../state/schedule.actions';

export default function ResourceDayCell({
  axis,
  row,
  day,
  addTop = 6,
  onShiftDrop,
}) {
  const dispatch = useDispatch();

  const companySettings = useSelector((state) => state.settings.company);
  const dropRef = useRef({});
  dropRef.current = {
    onShiftDrop,
    axis,
    row,
    day,
    companySettings,
    dispatch,
  };

  const [{ isOver, isScheduleDrop }, drop] = useDrop(() => ({
    accept: ['schedule', 'MonthShiftDayEntry', 'MonthMultiDayEntry'],
    collect: (monitor) => ({
      isOver: monitor.isOver() && monitor.canDrop(),
      isScheduleDrop: monitor.isOver()
        && monitor.canDrop()
        && monitor.getItemType() === 'schedule',
    }),
    canDrop: (item, monitor) => {
      const current = dropRef.current;
      if (monitor.getItemType() === 'schedule') return !!current.onShiftDrop && !!current.row;
      const { shift } = item;
      if (!current.row) return true; // Unassign;
      return (current.row.divisionIds ?? []).includes(shift?.divisionId);
    },
    drop: (item, monitor) => {
      const current = dropRef.current;
      if (monitor.getItemType() === 'schedule') {
        const dropArgs = {
          day: current.day,
          shift: item,
          shiftField: current.axis.shiftField,
          resourceId: current.row?.id,
        };
        setTimeout(() => current.onShiftDrop?.(dropArgs), 0);
        return;
      }

      const { resourceId: previousId, shift } = item;
      const newId = current.row?.id;
      const shiftStartDT = DateTime.fromMillis(shift.startTime);
      if (previousId === newId && shiftStartDT.hasSame(current.day, 'day')) return;

      const { emailNotification, pushNotification } = current.companySettings?.settings ?? {};
      const payload = prepareDragShiftUpdatePayload({
        shift,
        startOfNewDay: current.day,
        emailNotification,
        pushNotification,
        ...current.axis.getReassignArgs(previousId, newId),
      });
      const { dispatch: dispatchUpdate } = current;
      setTimeout(() => dispatchUpdate(updateShift(shift.id, payload)), 0);
    },
  }));

  const [showAddButton, setShowAddButton] = useState(false);

  const onAddClicked = useCallback(() => {
    dispatch(setBiweeklyModalConfig({
      visible: true,
      rowId: row?.id,
      date: day,
    }));
  }, [row, day]);

  return (
    <div
      className={`schedule-user-week-day-block${isScheduleDrop ? ' is-drop-over' : ''}`}
      onMouseEnter={() => setShowAddButton(true)}
      onMouseLeave={() => setShowAddButton(false)}
      ref={drop}
      style={isOver && !isScheduleDrop ? { boxShadow: '0px 0px 10px lightgray' } : undefined}
    >
      <div
        className="schedule-user-week-day-header"
        style={{
          visibility: showAddButton ? 'visible' : 'hidden',
          top: addTop,
        }}
        onClick={onAddClicked}
        role="button"
        onKeyDown={() => {}}
        tabIndex={-1}
      >
        <PlusOutlined />
      </div>
    </div>
  );
}

ResourceDayCell.propTypes = {
  axis: resourceAxisPropType.isRequired,
  row: resourceRowPropType,
  day: PropTypes.object.isRequired,
  addTop: PropTypes.number,
  onShiftDrop: PropTypes.func,
};

ResourceDayCell.defaultProps = {
  row: undefined,
  addTop: 6,
  onShiftDrop: undefined,
};
